import { useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { apiService } from "../services/api";
import { AuthService } from "../services/auth";
import { ApiError } from "../types/api";

interface UseSetPublicReturn {
  // Resolves to the public URL when publishing, "" when unpublishing, null on failure.
  setPublic: (videoKey: string, isPublic: boolean) => Promise<string | null>;
  isLoading: boolean;
  error: ApiError | null;
}

export function useSetPublic(): UseSetPublicReturn {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const setPublic = useCallback(
    async (videoKey: string, isPublic: boolean): Promise<string | null> => {
      const idToken = AuthService.getIdToken(auth);
      if (!idToken) {
        setError({ message: "User is not authenticated" });
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);

        const { slug } = await apiService.setVideoPublic(
          videoKey,
          isPublic,
          idToken,
        );
        return isPublic ? `${window.location.origin}/p/${slug}` : "";
      } catch (err) {
        setError({
          message:
            err instanceof Error ? err.message : "Failed to update video",
          status: (err as ApiError)?.status,
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  return { setPublic, isLoading, error };
}
